import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { 
  History, Search, Download, RefreshCw, Filter, Eye, User, FileText,
  Clock, Activity, ChevronLeft, ChevronRight, Calendar, Shield, BarChart3
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AuditTrailPage = () => {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState({
    action: '',
    resource_type: '',
    search: ''
  });
  const [selectedLog, setSelectedLog] = useState(null);
  const { toast } = useToast();

  const actionTypes = [
    'create', 'read', 'update', 'delete', 'login', 'logout', 
    'export', 'import', 'approve', 'reject', 'submit', 'assign', 'complete'
  ];

  const resourceTypes = [
    'incident', 'audit', 'training', 'document', 'user', 'company',
    'jha', 'ptp', 'observation', 'near_miss', 'permit', 'report', 'settings'
  ];

  useEffect(() => {
    fetchLogs();
    fetchStats();
  }, [page, filters]);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        page_size: '25'
      });
      if (filters.action) params.append('action', filters.action);
      if (filters.resource_type) params.append('resource_type', filters.resource_type);
      if (filters.search) params.append('search', filters.search);

      const response = await fetch(`${API_URL}/api/audit-trail/logs?${params}`);
      if (response.ok) {
        const data = await response.json();
        setLogs(data.logs);
        setTotalPages(data.pagination.total_pages);
      }
    } catch (error) {
      toast({ title: "Error", description: "Failed to load audit logs", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/audit-trail/stats/summary?days=30`);
      if (response.ok) {
        setStats(await response.json());
      }
    } catch (error) {
      console.error('Failed to load stats');
    }
  };

  const exportLogs = async (format) => {
    try {
      const response = await fetch(`${API_URL}/api/audit-trail/export?format=${format}`);
      if (response.ok) {
        const data = await response.json();
        toast({ title: "Export Ready", description: `Download available: ${data.download_url}` });
      }
    } catch (error) {
      toast({ title: "Error", description: "Failed to export logs", variant: "destructive" });
    }
  };

  const getActionColor = (action) => {
    const colors = {
      create: 'bg-green-100 text-green-800',
      read: 'bg-gray-100 text-gray-800',
      update: 'bg-blue-100 text-blue-800',
      delete: 'bg-red-100 text-red-800',
      login: 'bg-purple-100 text-purple-800',
      logout: 'bg-gray-100 text-gray-600',
      export: 'bg-yellow-100 text-yellow-800',
      import: 'bg-cyan-100 text-cyan-800',
      approve: 'bg-green-100 text-green-800',
      reject: 'bg-red-100 text-red-800',
      submit: 'bg-blue-100 text-blue-800',
      assign: 'bg-orange-100 text-orange-800',
      complete: 'bg-green-100 text-green-800'
    };
    return colors[action] || 'bg-gray-100 text-gray-800';
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return {
      date: date.toLocaleDateString(),
      time: date.toLocaleTimeString()
    };
  };

  const COLORS = ['#3b82f6', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

  return (
    <div className="space-y-6" data-testid="audit-trail-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <History className="w-7 h-7 text-blue-600" />
            Audit Trail
          </h1>
          <p className="text-gray-500 mt-1">Track all user activities and system changes</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => exportLogs('csv')} className="gap-2">
            <Download className="w-4 h-4" />
            Export CSV
          </Button>
          <Button variant="outline" onClick={() => exportLogs('json')} className="gap-2">
            <Download className="w-4 h-4" />
            Export JSON
          </Button>
        </div>
      </div>

      {/* Stats Overview */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-testid="audit-stats">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Activity className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.total_actions}</p>
                  <p className="text-sm text-gray-500">Total Actions (30 days)</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <FileText className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.by_action.find(a => a.action === 'create')?.count || 0}</p>
                  <p className="text-sm text-gray-500">Records Created</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <User className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.most_active_users?.length || 0}</p>
                  <p className="text-sm text-gray-500">Active Users</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-yellow-100 rounded-lg">
                  <Download className="w-5 h-5 text-yellow-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.by_action.find(a => a.action === 'export')?.count || 0}</p>
                  <p className="text-sm text-gray-500">Exports</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <Tabs defaultValue="logs" className="space-y-4">
        <TabsList>
          <TabsTrigger value="logs">Activity Logs</TabsTrigger>
          <TabsTrigger value="analytics">Analytics</TabsTrigger>
          <TabsTrigger value="compliance">Compliance Report</TabsTrigger>
        </TabsList>

        {/* Logs Tab */}
        <TabsContent value="logs" className="space-y-4">
          {/* Filters */}
          <Card data-testid="audit-filters">
            <CardContent className="p-4">
              <div className="flex flex-wrap gap-4">
                <div className="flex-1 min-w-[200px]">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      placeholder="Search logs..."
                      className="pl-10"
                      value={filters.search}
                      onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                      data-testid="search-input"
                    />
                  </div>
                </div>
                <Select value={filters.action || "all"} onValueChange={(v) => setFilters({ ...filters, action: v === "all" ? "" : v })}>
                  <SelectTrigger className="w-40" data-testid="action-filter">
                    <SelectValue placeholder="All Actions" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Actions</SelectItem>
                    {actionTypes.map((action) => (
                      <SelectItem key={action} value={action}>{action}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={filters.resource_type || "all"} onValueChange={(v) => setFilters({ ...filters, resource_type: v === "all" ? "" : v })}>
                  <SelectTrigger className="w-40" data-testid="resource-filter">
                    <SelectValue placeholder="All Resources" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Resources</SelectItem>
                    {resourceTypes.map((type) => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button variant="outline" onClick={fetchLogs} className="gap-2">
                  <RefreshCw className="w-4 h-4" />
                  Refresh
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Logs Table */}
          <Card data-testid="audit-logs-table">
            <CardContent className="p-0">
              {loading ? (
                <div className="flex items-center justify-center h-64">
                  <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="text-left p-4 font-medium text-gray-600">Timestamp</th>
                        <th className="text-left p-4 font-medium text-gray-600">User</th>
                        <th className="text-left p-4 font-medium text-gray-600">Action</th>
                        <th className="text-left p-4 font-medium text-gray-600">Resource</th>
                        <th className="text-left p-4 font-medium text-gray-600">Description</th>
                        <th className="text-center p-4 font-medium text-gray-600">Details</th>
                      </tr>
                    </thead>
                    <tbody>
                      {logs.map((log) => {
                        const ts = formatTimestamp(log.timestamp);
                        return (
                          <tr key={log.id} className="border-t hover:bg-gray-50">
                            <td className="p-4">
                              <div className="text-sm">{ts.date}</div>
                              <div className="text-xs text-gray-500">{ts.time}</div>
                            </td>
                            <td className="p-4">
                              <div className="font-medium text-sm">{log.user_name}</div>
                              <div className="text-xs text-gray-500">{log.user_email}</div>
                            </td>
                            <td className="p-4">
                              <Badge className={getActionColor(log.action)}>
                                {log.action}
                              </Badge>
                            </td>
                            <td className="p-4">
                              <div className="text-sm">{log.resource_type}</div>
                              <div className="text-xs text-gray-500 truncate max-w-[150px]">{log.resource_name}</div>
                            </td>
                            <td className="p-4">
                              <p className="text-sm text-gray-600 truncate max-w-[250px]">{log.description}</p>
                            </td>
                            <td className="p-4 text-center">
                              <Dialog>
                                <DialogTrigger asChild>
                                  <Button variant="ghost" size="sm" onClick={() => setSelectedLog(log)}>
                                    <Eye className="w-4 h-4" />
                                  </Button>
                                </DialogTrigger>
                                <DialogContent className="max-w-2xl">
                                  <DialogHeader>
                                    <DialogTitle>Audit Log Details</DialogTitle>
                                  </DialogHeader>
                                  {selectedLog && (
                                    <div className="space-y-4">
                                      <div className="grid grid-cols-2 gap-4">
                                        <div>
                                          <label className="text-sm text-gray-500">Timestamp</label>
                                          <p className="font-medium">{new Date(selectedLog.timestamp).toLocaleString()}</p>
                                        </div>
                                        <div>
                                          <label className="text-sm text-gray-500">User</label>
                                          <p className="font-medium">{selectedLog.user_name}</p>
                                          <p className="text-sm text-gray-500">{selectedLog.user_email}</p>
                                        </div>
                                        <div>
                                          <label className="text-sm text-gray-500">Action</label>
                                          <Badge className={getActionColor(selectedLog.action)}>{selectedLog.action}</Badge>
                                        </div>
                                        <div>
                                          <label className="text-sm text-gray-500">Resource</label>
                                          <p className="font-medium">{selectedLog.resource_type}</p>
                                          <p className="text-sm text-gray-500">{selectedLog.resource_id}</p>
                                        </div>
                                      </div>
                                      <div>
                                        <label className="text-sm text-gray-500">Description</label>
                                        <p className="mt-1">{selectedLog.description}</p>
                                      </div>
                                      {selectedLog.changes && (
                                        <div>
                                          <label className="text-sm text-gray-500">Changes</label>
                                          <pre className="mt-1 p-3 bg-gray-100 rounded text-sm overflow-auto">
                                            {JSON.stringify(selectedLog.changes, null, 2)}
                                          </pre>
                                        </div>
                                      )}
                                      <div className="grid grid-cols-2 gap-4 text-sm">
                                        <div>
                                          <label className="text-gray-500">IP Address</label>
                                          <p>{selectedLog.ip_address || 'N/A'}</p>
                                        </div>
                                        <div>
                                          <label className="text-gray-500">User Agent</label>
                                          <p className="truncate">{selectedLog.user_agent || 'N/A'}</p>
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                </DialogContent>
                              </Dialog>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Pagination */}
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-500">Page {page} of {totalPages}</p>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </TabsContent>

        {/* Analytics Tab */}
        <TabsContent value="analytics" className="space-y-4">
          {stats && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Actions Chart */}
              <Card>
                <CardHeader>
                  <CardTitle>Actions by Type</CardTitle>
                </CardHeader>
                <CardContent className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={stats.by_action} dataKey="count" nameKey="action" cx="50%" cy="50%" outerRadius={80} label>
                        {stats.by_action.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Resources Chart */}
              <Card>
                <CardHeader>
                  <CardTitle>Activity by Resource</CardTitle>
                </CardHeader>
                <CardContent className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={stats.by_resource} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" />
                      <YAxis dataKey="resource_type" type="category" width={100} />
                      <Tooltip />
                      <Bar dataKey="count" fill="#3b82f6" />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Daily Activity */}
              <Card className="lg:col-span-2">
                <CardHeader>
                  <CardTitle>Daily Activity (Last 30 Days)</CardTitle>
                </CardHeader>
                <CardContent className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={stats.daily_activity}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" tickFormatter={(v) => new Date(v).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} />
                      <YAxis />
                      <Tooltip />
                      <Line type="monotone" dataKey="count" stroke="#3b82f6" strokeWidth={2} />
                    </LineChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Most Active Users */}
              <Card className="lg:col-span-2">
                <CardHeader>
                  <CardTitle>Most Active Users</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {stats.most_active_users?.map((user, index) => (
                      <div key={user.user_id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-bold">
                            {index + 1}
                          </div>
                          <div>
                            <p className="font-medium">{user.user_name}</p>
                            <p className="text-sm text-gray-500">{user.user_id}</p>
                          </div>
                        </div>
                        <Badge variant="secondary">{user.actions} actions</Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>

        {/* Compliance Report Tab */}
        <TabsContent value="compliance" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-green-600" />
                Compliance Audit Report
              </CardTitle>
              <CardDescription>Generate reports for regulatory compliance</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium">Start Date</label>
                    <Input type="date" className="mt-1" />
                  </div>
                  <div>
                    <label className="text-sm font-medium">End Date</label>
                    <Input type="date" className="mt-1" />
                  </div>
                  <Button className="w-full gap-2">
                    <FileText className="w-4 h-4" />
                    Generate Compliance Report
                  </Button>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-medium mb-2">Report Includes:</h4>
                  <ul className="space-y-2 text-sm text-gray-600">
                    <li className="flex items-center gap-2">
                      <div className="w-1.5 h-1.5 bg-blue-600 rounded-full" />
                      Total system accesses
                    </li>
                    <li className="flex items-center gap-2">
                      <div className="w-1.5 h-1.5 bg-blue-600 rounded-full" />
                      Document access history
                    </li>
                    <li className="flex items-center gap-2">
                      <div className="w-1.5 h-1.5 bg-blue-600 rounded-full" />
                      User activity by role
                    </li>
                    <li className="flex items-center gap-2">
                      <div className="w-1.5 h-1.5 bg-blue-600 rounded-full" />
                      Sensitive data access log
                    </li>
                    <li className="flex items-center gap-2">
                      <div className="w-1.5 h-1.5 bg-blue-600 rounded-full" />
                      Security events summary
                    </li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AuditTrailPage;
